import { dateRangeUrl, request } from './api.js';
import type {
  DailyPerformanceResponse,
  DailyStylistStatsResponse,
  RatingAnalysisResponse,
  RequestInsightsResponse,
  RequestListResponse,
  RequestTrendsResponse,
  SavedStylingAiAnalysisResponse,
  StylingAiAnalysisHistoryItem,
  StylingDetailResponse,
} from '../types/styling-analysis.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリング分析 API クライアント。日別スタイリスト別統計・被キャンセル・エラー通知・ドリルダウン詳細・レーティング分析・リクエストインサイトを取得する。
 * @graph-connects /styling-analysis/daily-stylist-stats [calls] Daily per-stylist stats
 * @graph-connects /styling-analysis/daily-details [calls] Styling detail drilldown
 * @graph-connects /styling-analysis/request-trends [calls] Request trends
 * @graph-connects /styling-analysis/requests [calls] Request list
 * @graph-connects /styling-analysis/ai-trends [calls] AI trend analysis
 * @graph-connects /styling-analysis/daily-performance [calls] Daily performance
 * @graph-connects /styling-analysis/rating-analysis [calls] Rating analysis with first/repeat breakdown
 * @graph-connects /styling-analysis/request-insights [calls] Request insights
 */
export const stylingAnalysisApi = {
  getDailyStylistStats: (params?: {
    from?: string;
    to?: string;
  }): Promise<DailyStylistStatsResponse> =>
    request<DailyStylistStatsResponse>(
      dateRangeUrl('/styling-analysis/daily-stylist-stats', params?.from, params?.to),
    ),
  getDailyDetails: (params: {
    date: string;
    type: 'completed' | 'rejected' | 'error';
    stylistId?: string;
  }): Promise<StylingDetailResponse> => {
    const qs = new URLSearchParams();
    qs.set('date', params.date);
    qs.set('type', params.type);
    if (params.stylistId) qs.set('stylistId', params.stylistId);
    return request<StylingDetailResponse>(`/styling-analysis/daily-details?${qs.toString()}`);
  },
  getRequestTrends: (params?: { from?: string; to?: string }): Promise<RequestTrendsResponse> =>
    request<RequestTrendsResponse>(
      dateRangeUrl('/styling-analysis/request-trends', params?.from, params?.to),
    ),
  getRequests: (params?: {
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<RequestListResponse> => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return request<RequestListResponse>(
      query ? `/styling-analysis/requests?${query}` : '/styling-analysis/requests',
    );
  },
  runAiTrends: (params: { from?: string; to?: string }): Promise<SavedStylingAiAnalysisResponse> =>
    request('/styling-analysis/ai-trends', { method: 'POST', body: JSON.stringify(params) }),
  getAiTrendsLatest: (): Promise<SavedStylingAiAnalysisResponse | null> =>
    request('/styling-analysis/ai-trends/latest'),
  getAiTrendsHistory: (): Promise<{ items: StylingAiAnalysisHistoryItem[] }> =>
    request('/styling-analysis/ai-trends/history'),
  getAiTrendsDetail: (id: string): Promise<SavedStylingAiAnalysisResponse | null> =>
    request(`/styling-analysis/ai-trends/${encodeURIComponent(id)}`),
  getDailyPerformance: (params?: {
    from?: string;
    to?: string;
  }): Promise<DailyPerformanceResponse> =>
    request<DailyPerformanceResponse>(
      dateRangeUrl('/styling-analysis/daily-performance', params?.from, params?.to),
    ),
  getRatingAnalysis: (params?: {
    from?: string;
    to?: string;
    dateBase?: string;
  }): Promise<RatingAnalysisResponse> => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.dateBase) qs.set('dateBase', params.dateBase);
    const query = qs.toString();
    return request<RatingAnalysisResponse>(
      query ? `/styling-analysis/rating-analysis?${query}` : '/styling-analysis/rating-analysis',
    );
  },
  getRequestInsights: (params?: { from?: string; to?: string }): Promise<RequestInsightsResponse> =>
    request<RequestInsightsResponse>(
      dateRangeUrl('/styling-analysis/request-insights', params?.from, params?.to),
    ),
};
